// src/services/pointsService.js

import { supabase } from '../supabaseClient';
import { calculateAllStats } from '../utils/statsCalculator';

/**
 * Extrait les points depuis engineState et les prépare pour Supabase
 */
export const extractPointsFromMatch = (match) => {
  console.log('📊 Extraction des points du match:', match.id);
  
  if (!match.engineState || !match.engineState.points) {
    console.log('⚠️ Pas de points dans engineState');
    return [];
  }
  
  const points = match.engineState.points;
  console.log(`📝 ${points.length} points trouvés`);
  
  // Mapper chaque point vers le schéma Supabase
  const mappedPoints = points.map((point, index) => {
    // Extraire pointData (qui contient les détails techniques)
    const pd = point.pointData || {};
    
    return {
      matchId: match.id,
      pointNumber: point.pointNumero || (index + 1),
      setNumber: point.setNumero || 1,
      gameNumber: point.jeuNumero || 0,
      
      winner: point.winner,
      server: point.server,
      
      scoreAvant: point.scoreAvantPoint ? { jeuScore: point.scoreAvantPoint } : null,
      scoreApres: point.scoreApresPoint ? { jeuScore: point.scoreApresPoint } : null,
      
      // Code du point (famille + sous-type)
      pointCode: `${pd.familleCode || ''}_${pd.sousTypeCode || ''}`,
      
      // Détails techniques
      familleCode: pd.familleCode || null,
      sousTypeCode: pd.sousTypeCode || null,
      nbFrappesCode: pd.nbFrappesCode ? parseInt(pd.nbFrappesCode) : null,
      nbFrappesReel: pd.nbFrappesReel || null,
      resultatCode: pd.resultatCode || null,
      serviceReussite: pd.serviceReussite || null,
      zoneService: pd.zoneService || pd.zoneCode || null,
      effetCode: pd.effetCode || null,
      fauteDirecteCode: pd.fauteDirecteCode || null,
      
      positionServeur: pd.positionServeur || null,
      coteService: pd.coteService || null,
      
      // Vidéo (si disponible)
      videoStartTime: pd.videoStartTime || null,
      videoEndTime: pd.videoEndTime || null,
      videoUrl: match.videoUrl || null,
    };
  });
  
  console.log(`✅ ${mappedPoints.length} points mappés`);
  return mappedPoints;
};

/**
 * Synchronise les points d'un match vers Supabase
 * Utilise le batch processing pour les gros volumes
 */
export const syncPointsToSupabase = async (matchId, points) => {
  console.log(`🔄 Synchronisation de ${points.length} points pour le match ${matchId}`);
  
  if (points.length === 0) {
    console.log('⚠️ Aucun point à synchroniser');
    return { success: true, synced: 0 };
  }
  
  try {
    // 1. Vérifier si des points existent déjà pour ce match
    const { data: existingPoints } = await supabase
      .from('points')
      .select('pointNumber')
      .eq('matchId', matchId);
    
    if (existingPoints && existingPoints.length > 0) {
      console.log(`⚠️ ${existingPoints.length} points déjà présents, suppression...`);
      
      // Supprimer les anciens points (on va réinsérer la version à jour)
      const { error: deleteError } = await supabase
        .from('points')
        .delete()
        .eq('matchId', matchId);
      
      if (deleteError) throw deleteError;
    }
    
    // 2. Insérer par batch de 50 points (limite Supabase)
    const BATCH_SIZE = 50;
    let syncedCount = 0;
    
    for (let i = 0; i < points.length; i += BATCH_SIZE) {
      const batch = points.slice(i, i + BATCH_SIZE);
      
      console.log(`📤 Insertion batch ${Math.floor(i / BATCH_SIZE) + 1}/${Math.ceil(points.length / BATCH_SIZE)} (${batch.length} points)`);
      
      const { error: insertError } = await supabase
        .from('points')
        .insert(batch);
      
      if (insertError) throw insertError;
      
      syncedCount += batch.length;
    }
    
    console.log(`✅ ${syncedCount} points synchronisés pour le match ${matchId}`);
    
    return { success: true, synced: syncedCount };
    
  } catch (error) {
    console.error('❌ Erreur synchronisation points:', error.message);
    return { success: false, error: error.message };
  }
};

/**
 * Calcule et sauvegarde les statistiques d'un match
 * Utilise votre fonction calculateAllStats existante !
 */
export const calculateAndSaveMatchStatistics = async (matchId) => {
  console.log('📊 Calcul des statistiques pour le match:', matchId);
  
  try {
    // 1. Charger les points depuis la table définitive
    const { data: points, error: pointsError } = await supabase
      .from('points')
      .select('*')
      .eq('matchId', matchId)
      .order('pointNumber');
    
    if (pointsError) throw pointsError;
    
    if (!points || points.length === 0) {
      console.log('⚠️ Aucun point trouvé pour ce match');
      return { success: false, message: 'Aucun point trouvé' };
    }
    
    console.log(`📝 ${points.length} points chargés`);
    
    // 2. Convertir les points au format attendu par calculateAllStats
    // (Vos points Supabase → Format engineState.points)
    const formattedPoints = points.map(p => ({
      pointNumero: p.pointNumber,
      winner: p.winner,
      server: p.server,
      setNumero: p.setNumber,
      jeuNumero: p.gameNumber,
      scoreAvantPoint: p.scoreAvant?.jeuScore || '0-0',
      scoreApresPoint: p.scoreApres?.jeuScore || '0-0',
      formatSet: 'STD', // Vous pourriez l'ajouter dans la table points si besoin
      formatMatch: 'BEST_OF_3',
      pointData: {
        familleCode: p.familleCode,
        sousTypeCode: p.sousTypeCode,
        nbFrappesCode: p.nbFrappesCode,
        nbFrappesReel: p.nbFrappesReel,
        resultatCode: p.resultatCode,
        serviceReussite: p.serviceReussite,
        zoneCode: p.zoneService, // Mapping
        effetCode: p.effetCode,
        fauteDirecteCode: p.fauteDirecteCode,
      }
    }));
    
    // 3. Calculer les stats pour J1 avec VOTRE fonction existante
    console.log('🔢 Calcul des stats J1...');
    const statsJ1 = calculateAllStats(formattedPoints, 'J1');
    
    // 4. Calculer les stats pour J2
    console.log('🔢 Calcul des stats J2...');
    const statsJ2 = calculateAllStats(formattedPoints, 'J2');
    
    // 5. Sauvegarder dans la table match_statistics
    console.log('💾 Sauvegarde des stats dans Supabase...');
    
    // Préparer les données pour Supabase
    const prepareStatsForDB = (stats, player) => ({
      matchId: matchId,
      player: player,
      aces: stats.aces,
      doubleFaults: stats.dfs,
      firstServes: stats.firstServes,
      firstServesWon: stats.firstWon,
      firstServePct: stats.firstPct,
      firstServeWonPct: stats.firstWonPct,
      secondServes: stats.secondServes,
      secondServesWon: stats.secondWon,
      secondServeWonPct: stats.secondWonPct,
      totalServePoints: stats.totalServePoints,
      totalServeWon: stats.totalServeWon,
      serviceWonPct: stats.serviceWonPct,
      breakPointsFaced: stats.breakPointsFaced,
      breakPointsSaved: stats.breakPointsSaved,
      breakSavedPct: stats.breakSavedPct,
      breakPointsAgainst: stats.breakPointsAgainst,
      breakPointsConverted: stats.breakPointsConverted,
      breakConvertedPct: stats.breakConvertedPct,
      returnFirst: stats.returnFirst,
      returnFirstWon: stats.returnFirstWon,
      returnFirstPct: stats.returnFirstPct,
      returnSecond: stats.returnSecond,
      returnSecondWon: stats.returnSecondWon,
      returnSecondPct: stats.returnSecondPct,
      totalReturnPoints: stats.totalReturnPoints,
      totalReturnWon: stats.totalReturnWon,
      returnWonPct: stats.returnWonPct,
      winners: stats.winners,
      unForcedErrors: stats.unForcedErrors,
      netErrors: stats.netErrors,
      wideErrors: stats.wideErrors,
      longErrors: stats.longErrors,
      volleyWon: stats.volleyWon,
      volleyTotal: stats.volleyTotal,
      volleyWonPct: stats.volleyWonPct,
      gamesWon: stats.gamesWon,
      gamesServing: stats.gamesServing,
      gamesReturning: stats.gamesReturning,
      totalPoints: stats.totalPoints,
      zoneStats: stats.zoneStats
    });
    
    const statsToInsert = [
      prepareStatsForDB(statsJ1, 'J1'),
      prepareStatsForDB(statsJ2, 'J2')
    ];
    
    // Upsert (insert or update)
    const { error: insertError } = await supabase
      .from('match_statistics')
      .upsert(statsToInsert, {
        onConflict: 'matchId,player'
      });
    
    if (insertError) throw insertError;
    
    console.log('✅ Statistiques sauvegardées avec succès');
    
    return { 
      success: true, 
      statsJ1, 
      statsJ2,
      pointsCount: points.length
    };
    
  } catch (error) {
    console.error('❌ Erreur calcul/sauvegarde stats:', error.message);
    return { success: false, error: error.message };
  }
};

/**
 * Récupère les statistiques d'un match depuis la base
 */
export const getMatchStatistics = async (matchId) => {
  try {
    const { data, error } = await supabase
      .from('match_statistics')
      .select('*')
      .eq('matchId', matchId);
    
    if (error) throw error;
    
    if (!data || data.length === 0) {
      console.log('⚠️ Pas de stats en base, calcul à la volée nécessaire');
      return null;
    }
    
    const statsJ1 = data.find(s => s.player === 'J1');
    const statsJ2 = data.find(s => s.player === 'J2');
    
    return { statsJ1, statsJ2 };
    
  } catch (error) {
    console.error('❌ Erreur chargement stats:', error.message);
    return null;
  }
};

/**
 * Synchronise les points de TOUS les matchs existants
 */
export const syncAllMatchPoints = async () => {
  console.log('🔄 Synchronisation des points de tous les matchs...');
  
  // Charger la liste des matchs depuis localStorage
  const matchesList = JSON.parse(localStorage.getItem('matches') || '[]');
  
  if (matchesList.length === 0) {
    console.log('⚠️ Aucun match trouvé');
    return { success: true, total: 0, synced: 0 };
  }
  
  console.log(`📋 ${matchesList.length} matchs dans la liste`);
  
  let totalSynced = 0;
  const results = [];
  
  for (const matchFromList of matchesList) {
    const matchId = matchFromList.id || matchFromList.matchId;
    
    // Ne synchroniser que les matchs qui ont un ID Supabase (UUID)
    if (!matchId || matchId.length < 30) {
      console.log(`⏭️ Skip match ${matchId} (pas d'ID Supabase)`);
      continue;
    }
    
    try {
      // 🆕 CHARGER LE MATCH DEPUIS LE FICHIER INDIVIDUEL
      // C'est LÀ que engineState est à jour !
      const savedData = localStorage.getItem(`match_${matchId}`);
      
      if (!savedData) {
        console.log(`⏭️ Skip match ${matchId} (pas de fichier individuel)`);
        continue;
      }
      
      const match = JSON.parse(savedData);
      
      console.log(`📂 Match chargé: ${matchId}, engineState?`, !!match.engineState, 'points?', match.engineState?.points?.length);
      
      // Extraire les points depuis le fichier chargé
      const points = extractPointsFromMatch(match);
      
      if (points.length > 0) {
        // Synchroniser les points
        const result = await syncPointsToSupabase(matchId, points);
        
        if (result.success) {
          totalSynced += result.synced;
          
          // Calculer et sauvegarder les stats
          await calculateAndSaveMatchStatistics(matchId);
          
          results.push({ matchId: matchId, synced: result.synced });
        }
      } else {
        console.log(`⏭️ Skip match ${matchId} (aucun point)`);
      }
    } catch (error) {
      console.error(`❌ Erreur match ${matchId}:`, error.message);
      results.push({ matchId: matchId, error: error.message });
    }
  }
  
  console.log(`✅ Synchronisation terminée: ${totalSynced} points au total`);
  
  return { 
    success: true, 
    total: matchesList.length,
    synced: totalSynced,
    results 
  };
};

/**
 * Compte le nombre de points dans Supabase
 */
export const getPointsCount = async (matchId = null) => {
  try {
    let query = supabase
      .from('points')
      .select('id', { count: 'exact', head: true });
    
    if (matchId) {
      query = query.eq('matchId', matchId);
    }
    
    const { count, error } = await query;
    
    if (error) throw error;
    
    return { success: true, count };
    
  } catch (error) {
    console.error('❌ Erreur comptage points:', error.message);
    return { success: false, error: error.message };
  }
};